import { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../flux/stores/hooks';
import { createModelGroup, replaceModelGroup } from '../../../../flux/actions/apsThunks';
import { useApiAccessToken } from '../../../../utils/useApiAccessToken';
import { unresolvedLinks, displayName } from '../../../../flux/utils/rvtGraph';
import { DisciplineChips } from '../DisciplineChips/DisciplineChips';
import { ModelPicker } from '../ModelPicker/ModelPicker';
import './ModelGroupEditor.css';

function membersToMap(group) {
    const map = new Map();
    for (const member of group?.members ?? []) {
        map.set(member.lineage_urn, {
            name: member.name,
            disciplines: member.disciplines,
            missingInAcc: member.missing_in_acc,
        });
    }
    return map;
}

/**
 * Name, member list, discipline assignment and the "add models" picker for
 * one group — used for both creating a group (`group` is `null`) and
 * editing one. Save always sends the full member list: the API replaces a
 * group's membership wholesale, and the editor's local state already *is*
 * that request body.
 *
 * Each member's name is captured locally the moment it's added (from the
 * folder-contents row that added it, or from the existing group when
 * editing) — `/aps/rvt-models` is deliberately not consulted for it. That
 * catalog is used in exactly one place here: resolving names for the
 * unresolved-links tip, which is the one feature that's actually about the
 * link graph.
 */
export const ModelGroupEditor = ({
    projectId,
    group,
    allGroups,
    nodesByUrn,
    edges,
    onDone,
    onCancel,
}) => {
    const dispatch = useAppDispatch();
    const { getAccessToken } = useApiAccessToken();
    const mutation = useAppSelector(state => state.projects.aps[projectId]?.modelGroups.mutation)
        ?? { isSaving: false, error: null };

    const [name, setName] = useState(group?.name ?? '');
    const [members, setMembers] = useState(() => membersToMap(group));

    const selectedUrns = useMemo(() => new Set(members.keys()), [members]);

    const otherGroupsByUrn = useMemo(() => {
        const map = new Map();
        for (const other of allGroups) {
            if (other.object_id === group?.object_id) continue;
            for (const member of other.members) {
                const names = map.get(member.lineage_urn);
                if (names) names.push(other.name);
                else map.set(member.lineage_urn, [other.name]);
            }
        }
        return map;
    }, [allGroups, group?.object_id]);

    const unresolved = useMemo(
        () => unresolvedLinks(selectedUrns, edges, nodesByUrn),
        [selectedUrns, edges, nodesByUrn]
    );

    // A model reaching either of these two functions was just seen live —
    // either browsed straight from ACC, or resolved from the link graph for
    // the "Add it" tip — so it is definitely not missing.
    const addModel = (lineageUrn, modelName) => {
        setMembers(prev => {
            if (prev.has(lineageUrn)) return prev;
            const next = new Map(prev);
            next.set(lineageUrn, { name: modelName ?? null, disciplines: [], missingInAcc: false });
            return next;
        });
    };

    const toggleModel = (lineageUrn, modelName) => {
        setMembers(prev => {
            const next = new Map(prev);
            if (next.has(lineageUrn)) next.delete(lineageUrn);
            else next.set(lineageUrn, { name: modelName ?? null, disciplines: [], missingInAcc: false });
            return next;
        });
    };

    const removeModel = (lineageUrn) => {
        setMembers(prev => {
            const next = new Map(prev);
            next.delete(lineageUrn);
            return next;
        });
    };

    const setDisciplines = (lineageUrn, disciplines) => {
        setMembers(prev => {
            const next = new Map(prev);
            const entry = next.get(lineageUrn);
            next.set(lineageUrn, { ...entry, disciplines });
            return next;
        });
    };

    const trimmedName = name.trim();
    const hasEveryDiscipline = [...members.values()].every(m => m.disciplines.length > 0);
    const canSave = trimmedName.length > 0 && members.size > 0 && hasEveryDiscipline && !mutation.isSaving;

    const handleSave = async () => {
        if (!canSave) return;
        let accessToken;
        try {
            accessToken = await getAccessToken();
        } catch (error) {
            console.error('Failed to acquire API token to save the model group:', error);
            return;
        }
        if (!accessToken) return;

        const payloadMembers = [...members.entries()].map(([lineage_urn, entry]) => ({
            lineage_urn,
            disciplines: entry.disciplines,
        }));
        const action = group
            ? replaceModelGroup({ accessToken, projectId, groupId: group.object_id, name: trimmedName, members: payloadMembers })
            : createModelGroup({ accessToken, projectId, name: trimmedName, members: payloadMembers });

        const result = await dispatch(action);
        if (createModelGroup.fulfilled.match(result) || replaceModelGroup.fulfilled.match(result)) onDone();
    };

    return (
        <div className="mg-editor">
            <label className="pw-label" htmlFor="mg-name">Group name</label>
            <input
                id="mg-name"
                className="pw-input"
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={mutation.isSaving}
                autoFocus
            />
            {mutation.error?.status === 409 && (
                <p className="org-error">{mutation.error.message ?? `A group named "${trimmedName}" already exists.`}</p>
            )}

            <h4 className="mg-editor-subhead">Models in this group</h4>
            {members.size === 0 && <p className="pd-note">Pick models from the list below.</p>}
            {[...members.entries()].map(([lineageUrn, entry]) => (
                <div key={lineageUrn} className="mg-member-row">
                    <span className="mg-model-name">{entry.name ?? lineageUrn}</span>
                    {entry.missingInAcc === true && (
                        <span className="mg-model-missing">Missing from ACC</span>
                    )}
                    <DisciplineChips
                        value={entry.disciplines}
                        onChange={d => setDisciplines(lineageUrn, d)}
                    />
                    {entry.disciplines.length === 0 && <span className="mg-editor-flag">Pick a discipline</span>}
                    <button
                        type="button"
                        className="rules-btn-ghost mg-member-remove"
                        onClick={() => removeModel(lineageUrn)}
                        disabled={mutation.isSaving}
                    >
                        Remove
                    </button>
                </div>
            ))}

            {unresolved.length > 0 && (
                <div className="mg-unresolved">
                    {unresolved.map(({ from, to }) => (
                        <p key={`${from}->${to}`} className="pd-note">
                            {displayName(nodesByUrn.get(from), from)} links to{' '}
                            {displayName(nodesByUrn.get(to), to)}, which isn't in this group.{' '}
                            <button
                                type="button"
                                className="mg-unresolved-add"
                                onClick={() => addModel(to, nodesByUrn.get(to)?.name)}
                                disabled={mutation.isSaving}
                            >
                                Add it
                            </button>
                        </p>
                    ))}
                </div>
            )}

            <h4 className="mg-editor-subhead">Add models</h4>
            <ModelPicker
                projectId={projectId}
                otherGroupsByUrn={otherGroupsByUrn}
                selectedUrns={selectedUrns}
                onToggleModel={toggleModel}
                disabled={mutation.isSaving}
            />

            {mutation.error && mutation.error.status !== 409 && (
                <p className="org-error">{mutation.error.message}</p>
            )}

            <div className="pw-actions">
                <button type="button" className="rules-btn-primary" onClick={handleSave} disabled={!canSave}>
                    {mutation.isSaving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="rules-btn-ghost" onClick={onCancel} disabled={mutation.isSaving}>
                    Cancel
                </button>
            </div>
        </div>
    );
};
